import { formatCurrencyFromCents } from '@/utils/currencyHelpers'
import type {
  Rationale,
  RecommendationReasonCode,
  TierPrice,
} from './recommendation.types'

export const RECOMMENDED_BADGE_COPY = 'Recommended'

/** Exhaustive over the code union: a new reason code is a compile error, not a missing badge. */
const BADGE_COPY: Record<RecommendationReasonCode, string> = {
  caller_recommended: RECOMMENDED_BADGE_COPY,
  only_eligible_offer: RECOMMENDED_BADGE_COPY,
  best_tier_for_practice: RECOMMENDED_BADGE_COPY,
}

/** An unknown code renders no badge rather than erroring. */
export const badgeCopyForRationale = (
  rationale: Rationale | null
): string | null => {
  if (!rationale) return null
  return BADGE_COPY[rationale.code] ?? null
}

const INTERVAL_LABEL: Record<string, string> = {
  day: 'day',
  week: 'week',
  month: 'month',
  year: 'year',
}

export const PRICING_VARIES_COPY = 'Pricing varies'

/** Never assumes monthly: an unknown interval reads as "pricing varies". */
export const priceCopyForPrice = (price: TierPrice | undefined): string => {
  if (price === undefined) return PRICING_VARIES_COPY
  if (price.unitAmountInCents === null) return PRICING_VARIES_COPY
  const interval = INTERVAL_LABEL[price.interval]
  if (!interval) return PRICING_VARIES_COPY
  return `${formatCurrencyFromCents(price.unitAmountInCents, { hideDecimalsIfZero: true })} / ${interval}`
}

const INTERVAL_ABBREVIATION: Record<string, string> = {
  day: 'day',
  week: 'wk',
  month: 'mo',
  year: 'yr',
}

export type PriceParts = { amount: string; unit: string }

/**
 * Split for card layouts that size the amount and the unit differently (e.g. a large
 * "$100" next to a small "/mo"). Null when `priceCopyForPrice` would fall back to
 * "Pricing varies" — callers should render that string as a single line instead.
 */
export const priceDisplayParts = (
  price: TierPrice | undefined
): PriceParts | null => {
  if (price === undefined) return null
  if (price.unitAmountInCents === null) return null
  const interval = INTERVAL_ABBREVIATION[price.interval]
  if (!interval) return null
  return {
    amount: formatCurrencyFromCents(price.unitAmountInCents, {
      hideDecimalsIfZero: true,
    }),
    unit: `/${interval}`,
  }
}

/** Whole percent saved against twelve months at the monthly rate; null if not computable. */
export const annualSavingPercent = (
  monthly: TierPrice | undefined,
  annual: TierPrice | undefined
): number | null => {
  const monthlyAmount = monthly?.unitAmountInCents
  const annualAmount = annual?.unitAmountInCents
  if (!monthlyAmount || !annualAmount) return null
  const yearAtMonthlyRate = monthlyAmount * 12
  if (annualAmount >= yearAtMonthlyRate) return null
  return Math.round((1 - annualAmount / yearAtMonthlyRate) * 100)
}

export type AnnualPriceCopy = {
  /** Annual total divided by 12 — the large headline price for an annual plan. */
  proratedMonthly: string
  /** The plan's own monthly-billing rate, struck through for comparison. Null
   *  when there's no monthly price on this offer to compare against. */
  monthlyRate: string | null
}

/** Null when the annual price isn't a real, chargeable amount. */
export const annualPriceCopy = (
  monthly: TierPrice | undefined,
  annual: TierPrice | undefined
): AnnualPriceCopy | null => {
  const annualAmount = annual?.unitAmountInCents
  if (!annualAmount) return null
  return {
    proratedMonthly: formatCurrencyFromCents(Math.round(annualAmount / 12), {
      hideDecimalsIfZero: true,
    }),
    monthlyRate: monthly?.unitAmountInCents
      ? formatCurrencyFromCents(monthly.unitAmountInCents, {
          hideDecimalsIfZero: true,
        })
      : null,
  }
}

export type AnnualSavingLabelParts = { prefix: string; highlight: string }

/**
 * Parts for the Annual toggle's label, so the "Save X%" portion can render in an accent
 * color. Always quotes the largest saving as a flat figure — no "up to" hedge — since it's
 * shown as a compact pill next to a single toggle, not a sentence. Null when no offer has a
 * computable saving.
 */
export const annualSavingLabelParts = (
  savings: readonly (number | null)[]
): AnnualSavingLabelParts | null => {
  const real = savings.filter((saving): saving is number => saving !== null)
  if (real.length === 0) return null
  const best = Math.max(...real)
  return {
    prefix: 'Annual',
    highlight: `Save ${best}%`,
  }
}

export type TierCardCopy = {
  description: string
  /** What this tier doesn't include, that a higher tier does. Empty for the top tier. */
  missing: readonly string[]
}

/**
 * PROTOTYPE — static per-tier copy (description, "what's not included") that the
 * plan-recommendation API doesn't return today. Keyed by the five tiers the catalog
 * defines (see `MEMBERSHIP_TIERS` on the API). An unrecognized tier renders none of this —
 * no description, no missing section — rather than guessing at copy for it.
 *
 * The per-tier eyebrows ("Done-for-you books", "Books + taxes", "Group + S corporation")
 * were removed in CORE-8133: they restated the description a line below them, and the
 * third read as though a practice had to be both a group and an S corporation.
 */
const TIER_CARD_COPY: Record<string, TierCardCopy> = {
  lite: {
    description:
      'Your books kept by Heard every month, plus quarterly tax estimates. Annual tax filing not included.',
    missing: [
      'Annual tax prep and filing',
      'S corporation election and payroll',
    ],
  },
  essential: {
    description:
      'Done-for-you bookkeeping, financial reports, and quarterly and annual taxes for solo sole proprietors.',
    missing: ['Business return, S corporation and payroll'],
  },
  essential_plus: {
    description:
      'Done-for-you books and taxes for solo sole proprietors, plus a dedicated expert who gets to know your practice and your goals.',
    missing: ['Business return, S corporation and payroll'],
  },
  premium: {
    description:
      'Books, quarterly and annual taxes, S corporation support and payroll (add-on) for group practices and S corporations.',
    missing: [],
  },
  premium_plus: {
    description:
      'Books, taxes, S corporation support and payroll (add-on) for group practices and S corporations, plus a dedicated expert who gets to know your practice and your goals.',
    missing: [],
  },
}

export const tierCardCopyForTier = (tier: string): TierCardCopy | null =>
  TIER_CARD_COPY[tier] ?? null
